import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, FileX } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TabEmptyStateProps {
  /** Icon to display. Defaults to FileX */
  icon?: ReactNode;
  /** Main title for the empty state */
  title: string;
  /** Description text */
  description: string;
  /** Label for the action button */
  actionLabel?: string;
  /** Action to perform when button is clicked. If undefined, button is hidden */
  onAction?: () => void;
  /** Custom class for the container */
  className?: string;
}

/**
 * TabEmptyState - Standardized empty state for admin tabs
 * 
 * @example
 * <TabEmptyState
 *   title="No hay gastos"
 *   description="No se encontraron gastos que coincidan con los filtros."
 *   actionLabel="Crear Gasto"
 *   onAction={canCreate ? handleNew : undefined}
 * />
 */
const TabEmptyState = ({
  icon,
  title,
  description,
  actionLabel = 'Crear',
  onAction,
  className,
}: TabEmptyStateProps) => {
  return (
    <div
      className={cn(
        'bg-muted/50 border border-border rounded-lg p-8 text-center flex flex-col items-center',
        className
      )}
    >
      {icon && (
        <div className="text-muted-foreground mb-4">
          {icon}
        </div>
      )}
      
      <h3 className="text-foreground font-medium mb-2">{title}</h3>
      
      <p className="text-muted-foreground text-sm mb-4 max-w-md">
        {description}
      </p>

      {onAction && (
        <Button
          onClick={onAction}
          className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export default TabEmptyState;
